import { useEffect, useMemo, useState } from 'react'
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar, Cell, LabelList,
} from 'recharts'
import { fetchDashboard, type Dashboard as DashData } from '../lib/api'
import { aud, metalLabel } from '../lib/format'
import { Card, StatCard, SectionTitle, EmptyState } from '../components/ui'
import { assignCategoryColors, CHART, TIER_COLOR } from '../lib/palette'

export default function Dashboard() {
  const [data, setData] = useState<DashData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchDashboard().then(setData).catch((e) => setError(String(e)))
  }, [])

  if (error) return <EmptyState title="Couldn't load the dashboard" hint={error} />
  if (!data) return <p className="font-body text-sm text-ink/40">Loading…</p>

  const medianOf = (metal: string) =>
    data.median_price_by_metal.find((m) => m.metal === metal)?.median_price ?? null
  const hasData = data.totals.priced_variants > 0

  return (
    <div className="space-y-8">
      {/* First-run: seeded but never scraped — say exactly what to do next. */}
      {data.totals.active_variants === 0 && (
        <Card className="border-gold/40 bg-blush/40 p-4">
          <p className="font-display text-lg text-oxblood">No product data yet — 3 steps to first data</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 font-body text-sm text-ink/70">
            <li>Add <span className="font-medium">FIRECRAWL_API_KEY</span> and <span className="font-medium">ANTHROPIC_API_KEY</span> as <span className="font-medium">Secrets</span> (Cloudflare Pages → Settings → Variables and Secrets), then redeploy. If a scrape says <span className="italic">“Invalid token”</span>, the key was pasted wrong — re-enter it.</li>
            <li>Capture metal prices: <span className="font-medium">Admin → Spot prices → Refresh</span>.</li>
            <li>Run a scrape: <span className="font-medium">Admin → competitors → Run scrape</span>, then check the run log below it for any errors.</li>
          </ol>
        </Card>
      )}

      {/* Market snapshot cards */}
      <section>
        <SectionTitle note={`${data.totals.active_variants} active variants · ${data.totals.competitors} competitors`}>
          Market snapshot
        </SectionTitle>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <StatCard label="18ct Yellow" value={aud(medianOf('18ct yellow gold'), { round: true })} sub="median" />
          <StatCard label="18ct White" value={aud(medianOf('18ct white gold'), { round: true })} sub="median" />
          <StatCard label="Platinum" value={aud(medianOf('platinum 950'), { round: true })} sub="median" />
          <StatCard
            label="Custom vs Ready"
            value={`${aud(data.custom_vs_ready.custom.median, { round: true })}`}
            sub={`ready ${aud(data.custom_vs_ready.ready.median, { round: true })}`}
          />
          <StatCard label="Gold spot" value={data.spot.gold ? `$${data.spot.gold.toFixed(0)}/g` : '—'} sub="24ct AUD" />
          <StatCard label="Platinum spot" value={data.spot.platinum ? `$${data.spot.platinum.toFixed(0)}/g` : '—'} sub="AUD/g" />
        </div>
        {(data.totals.poa_count > 0 || data.totals.estimated_weight_count > 0) && (
          <p className="mt-2 font-body text-xs text-ink/40">
            {data.totals.poa_count} POA/quote-only · {data.totals.estimated_weight_count} estimated weights (flagged, not mixed with published)
          </p>
        )}
      </section>

      {/* Price distribution */}
      <section>
        <SectionTitle note="price vs band width · coloured by competitor">Price distribution</SectionTitle>
        <Card className="p-4">
          {hasData ? <DistributionChart data={data} /> : (
            <EmptyState title="No priced variants yet" hint="Run a scrape (Admin → run scrape) to populate. The chart lights up automatically." />
          )}
        </Card>
      </section>

      {/* Melt-premium league */}
      <section>
        <SectionTitle note="labour + margin above raw metal value · median AUD">Melt-premium league table</SectionTitle>
        <Card className="p-4">
          {data.melt_premium_league.some((r) => r.median_premium != null)
            ? <LeagueChart data={data} />
            : (
              <EmptyState
                title="Melt premium needs prices, weights and a spot snapshot"
                hint="Capture spot (Admin → refresh spot) and run a scrape. Where a competitor doesn't publish weight, the geometry estimate fills in — flagged."
              />
            )}
        </Card>
      </section>
    </div>
  )
}

function DistributionChart({ data }: { data: DashData }) {
  // Colour by competitor; top 8 by point count keep a hue, rest fold into Other.
  const byCompCount = useMemo(() => {
    const c = new Map<string, number>()
    data.distribution.forEach((d) => c.set(d.competitor_name, (c.get(d.competitor_name) ?? 0) + 1))
    return [...c.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name)
  }, [data])
  const colors = useMemo(() => assignCategoryColors(byCompCount), [byCompCount])

  const series = useMemo(() => {
    const groups = new Map<string, typeof data.distribution>()
    for (const d of data.distribution) {
      const key = colors.get(d.competitor_name) === '#898781' ? 'Other' : d.competitor_name
      ;(groups.get(key) ?? groups.set(key, []).get(key)!).push(d)
    }
    return [...groups.entries()]
  }, [data, colors])

  return (
    <ResponsiveContainer width="100%" height={340}>
      <ScatterChart margin={{ top: 8, right: 16, bottom: 24, left: 8 }}>
        <CartesianGrid stroke={CHART.grid} />
        <XAxis type="number" dataKey="band_width_mm" name="Width" unit="mm" domain={[0, 'dataMax + 1']}
          tick={{ fill: CHART.muted, fontSize: 12 }} stroke={CHART.axis}
          label={{ value: 'Band width (mm)', position: 'insideBottom', offset: -12, fill: CHART.muted, fontSize: 12 }} />
        <YAxis type="number" dataKey="price_aud" name="Price"
          tick={{ fill: CHART.muted, fontSize: 12 }} stroke={CHART.axis}
          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} width={48} />
        <ZAxis range={[45, 45]} />
        <Tooltip cursor={{ strokeDasharray: '3 3', stroke: CHART.axis }} content={<DistTooltip />} />
        {/* A single series needs no legend — the section title names it. */}
        {series.length > 1 && <Legend verticalAlign="top" align="right" wrapperStyle={{ fontSize: 12 }} />}
        {series.map(([name, points]) => (
          <Scatter key={name} name={name} data={points} fill={name === 'Other' ? '#898781' : colors.get(name)} fillOpacity={0.75} />
        ))}
      </ScatterChart>
    </ResponsiveContainer>
  )
}

function DistTooltip({ active, payload }: { active?: boolean; payload?: { payload: DashData['distribution'][0] }[] }) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="rounded-md border border-oxblood/15 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="font-body font-medium text-oxblood">{d.competitor_name}</p>
      <p className="font-body text-ink/60">{metalLabel(d.metal)} · {d.band_width_mm}mm · {d.is_custom ? 'custom' : 'ready-made'}</p>
      <p className="font-body tabular-nums text-ink">{aud(d.price_aud, { round: true })}</p>
    </div>
  )
}

function LeagueChart({ data }: { data: DashData }) {
  const rows = data.melt_premium_league.filter((r) => r.median_premium != null)
  return (
    <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 40)}>
      <BarChart layout="vertical" data={rows} margin={{ top: 4, right: 72, bottom: 4, left: 8 }}>
        <CartesianGrid stroke={CHART.grid} horizontal={false} />
        <XAxis type="number" tick={{ fill: CHART.muted, fontSize: 12 }} stroke={CHART.axis}
          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
        <YAxis type="category" dataKey="name" width={150} tick={{ fill: CHART.ink, fontSize: 12 }} stroke={CHART.axis} />
        <Tooltip cursor={{ fill: CHART.grid, fillOpacity: 0.4 }}
          formatter={(v: number) => [aud(v, { round: true }), 'median premium']} />
        <Bar dataKey="median_premium" radius={[0, 4, 4, 0]} barSize={20}>
          {rows.map((r) => (
            <Cell key={r.competitor_id} fill={r.tier ? TIER_COLOR[r.tier] ?? CHART.gold : CHART.gold} />
          ))}
          <LabelList dataKey="median_premium" position="right"
            formatter={(v: number) => aud(v, { round: true })}
            style={{ fill: CHART.muted, fontSize: 11 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}
